import { IconButton, Tooltip } from '@mui/material';
import React, { memo } from 'react';
import Flex from 'ui/Flex/Flex';
import ChatIcon from '@mui/icons-material/Chat';
import { useTranslation } from 'react-i18next';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { CellRendererParams } from 'routes/BudgetModule/config/types';
import { ProjectionStatuses } from 'business/constants';
import {
    AbstractProjectionDetailsT,
    AtterrissageProjectionDetails,
    ProjectionsDimensionsMapping,
    ProjectionT,
} from '../helpers/types';
import ProjectionStatus from './ProjectionStatus';
import ProjectionSidePanel from './ProjectionSidePanel';
import { UpdateProjectionDetailsParams } from '../helpers/colDefinitions';
import { getVentilationFromNode } from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/helpers/dataHelpers';

type DrawerEventT = React.KeyboardEvent | React.MouseEvent;

export type CellRendererStatusAndSidePanelValueT = {
    id: number | undefined;
    atterrissageStatus?: ProjectionStatuses | undefined;
    pluriannualStatus?: ProjectionStatuses | undefined;
    resourceSoftwareId: string | undefined;
    projectionDetailAmount?:
        | AtterrissageProjectionDetails.WithMetadata['projectionDetails.amount']
        | undefined;
    projectionDetailComment?:
        | AtterrissageProjectionDetails.WithMetadata['projectionDetails.comment']
        | undefined;
};

export type CellRendererParamsProjectionStatusAndSidePanelT = {
    resourceSoftwareId: string | undefined;
    budgetProposalId: number;
    exercice: number;
    dimensionsMapping: ProjectionsDimensionsMapping;
    projectionIdLabelMapping: Record<string, string>;
};

type CellRendererProjectionStatusAndSidePanelT = CellRendererParams<
    AbstractProjectionDetailsT,
    CellRendererStatusAndSidePanelValueT
> & {
    statusField: 'pluriannualProjectionStatus' | 'atterrissageStatus';
    customParams: CellRendererParamsProjectionStatusAndSidePanelT;
    updateProjectionParams: UpdateProjectionDetailsParams;
};

const ProjectionStatusSidePanelCellRenderer = ({
    updateProjectionParams,
    customParams,
    statusField,
    value,
    node,
}: CellRendererProjectionStatusAndSidePanelT): React.ReactElement | null => {
    const [isSidePanelOpen, setIsSidePanelOpen] = React.useState(false);
    const { budgetProposalId, exercice } = customParams;
    const { t } = useTranslation();
    const { budgetProposal } = updateProjectionParams;

    if (!value) {
        return null;
    }

    const {
        id,
        atterrissageStatus,
        pluriannualStatus,
        projectionDetailAmount,
        projectionDetailComment,
        resourceSoftwareId,
    } = value;

    if (
        !id ||
        (!atterrissageStatus && !pluriannualStatus) ||
        resourceSoftwareId === undefined
    ) {
        return <></>;
    }
    const projection: Pick<ProjectionT, 'id' | 'budgetProposalId'> &
        Partial<
            Pick<
                ProjectionT,
                'atterrissageStatus' | 'pluriannualProjectionStatus'
            >
        > = {
        id,
        atterrissageStatus,
        pluriannualProjectionStatus: pluriannualStatus,
        budgetProposalId,
    };
    const toggleDrawer = (event: DrawerEventT, open: boolean) => {
        if (
            'key' in event &&
            event.type === 'keydown' &&
            ['Tab', 'Shift'].includes(event.key)
        ) {
            return;
        }
        setIsSidePanelOpen(open);
    };
    const onSidePanelButtonClick = (e: DrawerEventT) => {
        if (statusField !== 'atterrissageStatus') return;
        toggleDrawer(e, true);
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetOverview.projections.atterrissage
                .detailsClicked,
        );
    };
    const dimensions = getVentilationFromNode(node);

    const canEditStatus =
        statusField === 'atterrissageStatus' ||
        (statusField === 'pluriannualProjectionStatus' &&
            budgetProposal.canEditPluriannualProjections);
    const shouldShowSidePanelButton =
        statusField === 'atterrissageStatus' && node.leafGroup;

    const canEdit =
        (canEditStatus && node?.allLeafChildren?.[0]?.data?.canEdit) ?? false;
    // we hide the side panel button if the row is a footer, because it does not make sense
    // to edit the details of the total row
    const openSidePanelButton = shouldShowSidePanelButton ? (
        <>
            <Tooltip title={t('budget.commentButton')}>
                <IconButton
                    color="primary"
                    onClick={onSidePanelButtonClick}
                    size="large"
                    sx={{ p: 0 }}
                >
                    <ChatIcon />
                </IconButton>
            </Tooltip>
            <ProjectionSidePanel
                open={isSidePanelOpen}
                onClose={(e: DrawerEventT) => toggleDrawer(e, false)}
                resourceSoftwareId={resourceSoftwareId}
                dimensions={dimensions}
                canEdit={canEdit}
                exercice={exercice}
                projectionDetailAmount={projectionDetailAmount ?? 0}
                projectionDetailComment={projectionDetailComment}
                updateProjectionParams={updateProjectionParams}
            />
        </>
    ) : (
        <></>
    );

    const isResourceGroup =
        node.field === updateProjectionParams.resourceSoftwareIdField;
    const cell = isResourceGroup ? (
        <ProjectionStatus
            resourceSoftwareId={resourceSoftwareId}
            projection={projection}
            statusField={statusField}
            canEdit={canEdit}
            budgetProposal={budgetProposal}
            isDaf
        />
    ) : (
        openSidePanelButton
    );
    return (
        <Flex justify="center" align="center">
            {cell}
        </Flex>
    );
};

export default memo(
    ProjectionStatusSidePanelCellRenderer,
    (prevProps, nextProps) => {
        const isAtterrissageStatusEqual =
            prevProps.value?.atterrissageStatus ===
            nextProps.value?.atterrissageStatus;
        const isPluriannualStatusEqual =
            prevProps.value?.pluriannualStatus ===
            nextProps.value?.pluriannualStatus;
        const isCommentEqual =
            prevProps.value?.projectionDetailComment ===
            nextProps.value?.projectionDetailComment;
        const isAmountEqual =
            prevProps.value?.projectionDetailAmount ===
            nextProps.value?.projectionDetailAmount;
        return (
            isAtterrissageStatusEqual &&
            isPluriannualStatusEqual &&
            isCommentEqual &&
            isAmountEqual
        );
    },
);
